import { X, Loader2, AlertCircle, RefreshCw } from 'lucide-react'
import { useArticleChangelog } from '../../api/hooks'
import type { WireChangelogEntry } from '../../api/types'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(ts: number): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(ts))
}

/** Shorten a full LDAP DN like "CN=John Smith,OU=Users,DC=rewire,DC=local" → "John Smith" */
function shortDn(dn: string): string {
  const cn = dn.match(/^CN=([^,]+)/i)?.[1]
  return cn ?? dn
}

function LabelBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block shrink-0 rounded bg-fill px-1.5 py-0.5 text-[10px] font-semibold capitalize tracking-wide text-fg-secondary">
      {children}
    </span>
  )
}

/**
 * Renders a changelog description string.
 *
 * Splits on newlines so multi-field changes each get their own row.
 * Lines starting with **label**: are rendered as a badge + prose.
 * PnSm markers (**P2S3**) get a "Para 2 · Sent 3" badge.
 */
function DescriptionText({ text }: { text: string }) {
  const lines = text
    .split(/\n/)
    .map(l => l.replace(/^[ \t]+|[ \t]+$/g, ''))
    .filter(Boolean)

  if (lines.length === 1 && !lines[0].startsWith('**')) {
    return <span className="italic text-fg-secondary/70">{lines[0]}</span>
  }

  return (
    <ul className="m-0 list-none space-y-2 p-0">
      {lines.map((line, i) => {
        // PnSm location marker: **P2S3**
        const locMatch = line.match(/^\*\*P(\d+)S(\d+)\*\*[:\s]*(.*)$/)
        if (locMatch) {
          return (
            <li key={i} className="flex flex-wrap items-baseline gap-2">
              <LabelBadge>Para {locMatch[1]} · Sent {locMatch[2]}</LabelBadge>
              {locMatch[3] && <span className="text-xs text-fg">{locMatch[3].replace(/^:\s*/, '')}</span>}
            </li>
          )
        }
        // Field label: **field name**: description
        const fieldMatch = line.match(/^\*\*([^*]+)\*\*[:\s](.+)$/)
        if (fieldMatch) {
          return (
            <li key={i} className="flex flex-wrap items-baseline gap-2">
              <LabelBadge>{fieldMatch[1]}</LabelBadge>
              <span className="text-xs text-fg">{fieldMatch[2]}</span>
            </li>
          )
        }
        // Bold-only line with no trailing prose (e.g. "**Paragraph removed.**")
        const boldOnlyMatch = line.match(/^\*\*([^*]+)\*\*\.?$/)
        if (boldOnlyMatch) {
          return (
            <li key={i} className="text-xs font-semibold text-fg">
              {boldOnlyMatch[1]}
            </li>
          )
        }
        // Plain line
        return <li key={i} className="text-xs text-fg">{line}</li>
      })}
    </ul>
  )
}

// ─── Entry row ────────────────────────────────────────────────────────────────

function ChangelogRow({ entry }: { entry: WireChangelogEntry }) {
  return (
    <li className="border-b border-line py-4 last:border-b-0">
      <div className="mb-2.5 flex items-center gap-2">
        <span className="text-xs font-semibold text-fg">{shortDn(entry.changedBy)}</span>
        {entry.version && (
          <span className="rounded bg-accent-primary-fill px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-accent-primary-fg">
            {entry.version.toUpperCase()}
          </span>
        )}
        <span className="ml-auto text-[10px] text-fg-secondary">{fmtDate(entry.changedAt)}</span>
      </div>
      <DescriptionText text={entry.description} />
    </li>
  )
}

// ─── ArticleChangelogDrawer ───────────────────────────────────────────────────

interface Props {
  draftId: string
  open: boolean
  onClose: () => void
}

export function ArticleChangelogDrawer({ draftId, open, onClose }: Props) {
  const { data, isPending, isError, refetch, isFetching } = useArticleChangelog(draftId, open)

  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-30 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Draft changelog"
        className="fixed right-0 top-[3rem] z-40 flex h-[calc(100vh-3rem)] w-[480px] flex-col bg-surface shadow-2xl"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-line px-6 py-4">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
              Changelog
            </p>
            <p className="mt-0.5 text-sm font-semibold text-fg">Revision History</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className="rounded p-1.5 text-fg-secondary transition-colors hover:bg-fill hover:text-fg disabled:opacity-40"
              aria-label="Refresh changelog"
              title="Refresh"
            >
              <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
              aria-label="Close changelog"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isPending && (
            <div className="flex items-center gap-2 text-fg-secondary">
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
              <span className="text-xs">Loading changelog…</span>
            </div>
          )}

          {isError && (
            <div className="flex items-start gap-2 rounded border border-error-line bg-error-fill p-3">
              <AlertCircle size={14} className="mt-0.5 shrink-0 text-error-fg" aria-hidden="true" />
              <div>
                <p className="text-xs font-semibold text-error-fg">Failed to load changelog</p>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="mt-1 text-[11px] text-error-fg underline hover:no-underline"
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {data && data.length === 0 && (
            <p className="text-xs text-fg-secondary">No changelog entries yet.</p>
          )}

          {data && data.length > 0 && (
            <ol className="list-none p-0 m-0">
              {[...data].reverse().map((entry, i) => (
                <ChangelogRow key={i} entry={entry} />
              ))}
            </ol>
          )}
        </div>
      </aside>
    </>
  )
}
